import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LocationsApiService, PartialCeLocation } from '@kempoweroyj/ngx-locations-api-client';
import { ReportingApiService } from '@kempoweroyj/ngx-reporting-api-client';
import { cloneDeep, sortBy } from 'lodash';
import { AuthService } from 'providers/auth.service';
import { BehaviorSubject, combineLatest, forkJoin, of, Subject, timer } from 'rxjs';
import { bufferToggle, distinctUntilChanged, map, mergeMap, switchMap, tap, throttleTime } from 'rxjs/operators';
import { CeLocationRow, LocationsService } from 'shared/stations-service/locations.service';
import { Satellite, StationsService } from 'shared/stations-service/stations.service';
import { StationStatusInfo } from './station-status-info';
import moment from 'moment';
import {
    calculateDurationAndZoomLevel,
    DownloadLocationPowerGraphAsExcelRequest,
    TimelineZoomLevel,
} from 'locations/station-dialog/status/timeline-controls/timeline-controls.component';
import { PopupService } from 'shared/popup-service/popup.service';
import { GoogleAnalyticsService } from '../../../google-analytics.service';
import { ChargingStationsApiService } from 'api/charging_stations.api';
import { EvseApiService } from '@kempoweroyj/ngx-evse-api-client';
import { StationCapabilitiesService } from 'shared/stations-service/station-capabilities.service';

interface StatusPageQueryParameters {
    zoomLevel?: TimelineZoomLevel;
    seekTime?: string;
    startTime?: string;
    endTime?: string;
}

const storageNameForPreferGridView = 'chargeye-prefer-grid-view';

@Component({
    selector: 'ce-location-status',
    templateUrl: './location-status.component.html',
    styleUrls: ['./location-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class LocationStatusComponent implements OnInit {
    mapData: { location: CeLocationRow; stationsInfo: StationStatusInfo[] } = { location: null, stationsInfo: [] };

    locationId: string;
    location: CeLocationRow;

    stationsInfo: StationStatusInfo[] = [];

    stations: { [stationId: string]: StationStatusInfo } = {};

    stationIds: string[] = [];
    satellites: Satellite[] = [];

    allowScheduleView = false;

    hideWorldMap = true; // hideWorldMap > showWorldMap (for hiding in basic license)
    showWorldMap = false;
    showWorldMapFirstTime = false;
    mapMaximized = false;

    hideExcelExport = true;

    mapDataChanged$ = new BehaviorSubject<void>(undefined); // Subject that is passed to the child component (map.component.ts) to notify that the map data is changed
    loadingMapData$ = new BehaviorSubject<boolean>(true); // Subject that is passed to the child component (map.component.ts) and invoked when the map data is loading

    includesKempowerHw: boolean;

    private _startTime: string = moment().startOf('day').toISOString();
    private _endTime: string = moment().endOf('day').toISOString();
    private _seekTime: string;
    private _zoomLevel: TimelineZoomLevel = 'DAY';
    get startTime() {
        return this._startTime;
    }
    set startTime(value: string) {
        this._nav({ startTime: value });
    }
    get endTime() {
        return this._endTime;
    }
    set endTime(value: string) {
        this._nav({ endTime: value });
    }
    get seekTime() {
        return this._seekTime || new Date().toISOString();
    }
    set seekTime(value: string) {
        this._nav({ seekTime: value });
    }
    get zoomLevel() {
        return this._zoomLevel;
    }
    set zoomLevel(value: TimelineZoomLevel) {
        this._nav({ zoomLevel: value });
    }
    private _queryParamUpdate$ = new Subject<StatusPageQueryParameters>();
    goToSchedule() {
        this.router.navigate(['../scheduling'], { relativeTo: this.route });
    }

    private _nav(params: StatusPageQueryParameters) {
        this._queryParamUpdate$.next(params);
    }

    private listenToNav() {
        this._queryParamUpdate$
            .pipe(
                bufferToggle(this._queryParamUpdate$.pipe(throttleTime(0)), () => timer(0)),
                untilDestroyed(this)
            )
            .subscribe((allParams) => {
                const params: StatusPageQueryParameters = allParams.reduce(
                    (all, p) => ({ ...all, ...p }),
                    {} as StatusPageQueryParameters
                );
                const now = new Date();
                const queryParams: StatusPageQueryParameters = {
                    ...(params.seekTime !== undefined
                        ? {
                              seekTime:
                                  params.seekTime === null
                                      ? null
                                      : moment(params.seekTime).isAfter(now)
                                        ? null
                                        : params.seekTime,
                          }
                        : {}), // cannot be in future
                    ...(params.zoomLevel !== undefined
                        ? { zoomLevel: params.zoomLevel === null ? null : params.zoomLevel }
                        : {}),
                    ...(params.startTime !== undefined
                        ? {
                              startTime:
                                  params.startTime === null
                                      ? null
                                      : moment(params.startTime).isAfter(now)
                                        ? now.toISOString()
                                        : params.startTime,
                          }
                        : {}), // cannot be in future
                    ...(params.endTime !== undefined
                        ? { endTime: params.endTime === null ? null : params.endTime }
                        : {}),
                };
                this.router.navigate([], {
                    queryParams,
                    queryParamsHandling: 'merge',
                });
            });
    }

    constructor(
        private cdRef: ChangeDetectorRef,
        private route: ActivatedRoute,
        private locationsService: LocationsService,
        private locationsApi: LocationsApiService,
        private stationsService: StationsService,
        private authService: AuthService,
        private reportingApi: ReportingApiService,
        private popup: PopupService,
        private router: Router,
        private googleAnalyticsService: GoogleAnalyticsService,
        private stationsApi: ChargingStationsApiService,
        private evseApi: EvseApiService,
        private stationCapabilitiesService: StationCapabilitiesService
    ) {}

    ngOnInit() {
        this.stationsService.resetSeek();
        this.listenToNav();

        this.googleAnalyticsService.updateVirtualPageView(this.mapMaximized, this.showWorldMap);

        // Permissions
        this.authService.permissions$.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((perms) => {
            // Hide world map
            this.hideWorldMap = !perms.hasAccess('fleet.chargingStation.overview.showWorldMap');
            // this.changeVirtualViewStatus();
            this.allowScheduleView = perms.hasAccess(['depotMaster.access', 'ev.scheduling.access']);
            // Hide power usage excel export (based on lambda requested perms)
            this.hideExcelExport = !perms.hasAccess('fleet.group.read');
            this.cdRef.markForCheck();
        });

        const locationId$ = this.route.parent.parent.params.pipe(
            map((params) => params.id as string),
            distinctUntilChanged()
        );

        const location$ = locationId$.pipe(
            tap(() => {
                this.stations = {};
                this.stationsInfo = [];
                this.stationIds = [];
                this.satellites = [];
                this.showWorldMap = false;
                this.showWorldMapFirstTime = false;
                this.location = null;
                this.cdRef.markForCheck();
            }),
            switchMap((locationId) => {
                this.locationId = locationId;
                return this.locationsApi.getLocation(locationId);
            }),
            untilDestroyed(this)
        );

        location$.subscribe((location) => {
            this.location = location.location;
            this.mapData.location = location.location;

            this.loadingMapData$.next(true);

            // World map teaser
            if (
                this.location.bounds &&
                !this.showWorldMapFirstTime &&
                localStorage.getItem(storageNameForPreferGridView) !== 'true'
            ) {
                this.showWorldMap = true;
                this.showWorldMapFirstTime = true;
            }

            this.cdRef.markForCheck();
        });

        this.route.queryParamMap.pipe(untilDestroyed(this)).subscribe((q) => {
            const seekTime = q.get('seekTime');
            const startTime = q.get('startTime') || moment().startOf('day').toISOString();
            const endTime = q.get('endTime') || moment().endOf('day').toISOString();
            const zoomLevel = (q.get('zoomLevel') as TimelineZoomLevel) || 'DAY';
            this._seekTime = seekTime;
            this._startTime = startTime;
            this._endTime = endTime;
            this._zoomLevel = zoomLevel;

            this.updateStartEndTime();

            this.cdRef.markForCheck();
        });

        const stationsInfo$ = locationId$.pipe(
            switchMap((locationId) => this.locationsService.locationStations$(locationId)),
            map((stations) => stations.map((s) => s.stationId)),
            tap((stationIds) => {
                this.stationIds = stationIds;
                this.checkStationsCapabilities();
                this.cdRef.markForCheck();
            }),
            switchMap(() => timer(0, 5000)), // Poll every 5 seconds
            mergeMap(() => {
                const stationIds = this.stationIds;
                const observables = stationIds.map((stationId) =>
                    combineLatest([
                        this.stationsApi.getChargingStation(stationId, { stationTenants: true }),
                        this.evseApi.getEvsesForChargingStation(stationId),
                    ])
                );
                if (observables.length === 0) {
                    // forkJoin below would wait forever if we have no stations
                    return of({});
                }

                // Use forkJoin to wait for all the observables to complete
                return forkJoin(observables).pipe(
                    map((combinedResults) => {
                        const stationsInfo = {};

                        combinedResults.forEach(([station, connectors]) => {
                            const value = { station, connectors } as StationStatusInfo;
                            stationsInfo[value.station.station.stationId] = value;
                        });

                        return stationsInfo;
                    })
                );
            }),
            untilDestroyed(this)
        );

        stationsInfo$.subscribe((stationsInfo: { [stationId: string]: StationStatusInfo }) => {
            this.stations = stationsInfo;
            this.stationsInfo = sortBy(Object.values(this.stations), (s) => s.station?.station?.name);

            this.updateSatellites();

            this.loadingMapData$.next(false);
            this.mapDataChanged$.next();
        });
    }

    async checkStationsCapabilities() {
        // check if all/some charging stations in the location are Kempower HW or not
        try {
            this.includesKempowerHw = (
                await Promise.all(
                    this.stationIds.map((stationId) =>
                        this.stationCapabilitiesService.isCapableOf(stationId, 'HardwareKempower')
                    )
                )
            ).some((isKempower) => isKempower);
            this.cdRef.markForCheck();
        } catch (e) {
            console.error('Error checking Kempower HW', e);
        }
    }

    updateStartEndTime() {
        const seekTime = this.seekTime;

        if (this.zoomLevel === 'WEEK') {
            this._startTime = moment(seekTime).startOf('isoWeek').toISOString();
            this._endTime = moment(seekTime).endOf('isoWeek').toISOString();
        } else if (this.zoomLevel === 'DAY') {
            this._startTime = moment(seekTime).startOf('day').toISOString();
            this._endTime = moment(seekTime).endOf('day').toISOString();
        } else if (this.zoomLevel === 'HOUR') {
            this._startTime = moment(seekTime).startOf('hour').toISOString();
            this._endTime = moment(seekTime).endOf('hour').toISOString();
        } else if (this.zoomLevel === 'CUSTOM') {
            // Check from params if time is valid
            const { isValidDuration, validEndTime } = calculateDurationAndZoomLevel(this.startTime, this.endTime);

            this._startTime = this.startTime;
            if (isValidDuration) {
                this._endTime = this.endTime;
            } else {
                this._endTime = validEndTime;
            }
        }

        this.cdRef.markForCheck();
    }
    toggleMap(value: boolean = undefined) {
        if (!value) {
            this.mapMaximized = !this.mapMaximized;
        } else {
            this.mapMaximized = value;
        }
        this.googleAnalyticsService.updateVirtualPageView(this.mapMaximized, this.showWorldMap);
        this.cdRef.markForCheck();
    }

    changeMapStatus(newMapStatus: boolean) {
        this.showWorldMap = newMapStatus;
        localStorage.setItem(storageNameForPreferGridView, String(!this.showWorldMap));
        this.googleAnalyticsService.updateVirtualPageView(this.mapMaximized, this.showWorldMap);
        this.cdRef.markForCheck();
    }

    locationChangedHandler(changed: PartialCeLocation) {
        this.loadingMapData$.next(true);

        this.mapData.location = {
            ...cloneDeep(this.location),
            ...changed,
        };

        this.cdRef.markForCheck();
    }

    updateSatellites() {
        this.satellites = [];
        for (const stationInfo of this.stationsInfo) {
            const connectors = stationInfo.connectors.filter((connector) => connector.active);
            if (connectors.length > 0) {
                this.satellites = [
                    ...this.satellites,
                    ...this.stationsService.getSatellites(stationInfo.station, connectors),
                ];
            }
        }
        this.satellites = this.satellites
            .filter((s) => s.connectors.length > 0)
            .sort((a, b) => a.station?.station?.name.localeCompare(b.station?.station?.name));
        this.cdRef.markForCheck();
    }

    async downloadLocationPowerUsageExcel(request: DownloadLocationPowerGraphAsExcelRequest) {
        this.popup.neutral(`Generating Excel..`);
        this.cdRef.markForCheck();

        try {
            const { locationUid, queryParameters } = request;
            const downloadRes = await this.reportingApi.downloadLocationPowerGraphAsExcel(locationUid, queryParameters);

            this.popup.success(`Excel downloaded`);
            this.cdRef.markForCheck();

            // download the file
            window.open(downloadRes.downloadUrl, '_blank');
        } catch (error) {
            console.error(error);
            this.popup.error(`Excel generation failed`);
            this.cdRef.markForCheck();
        }
    }
}
